import { where } from 'underscore';
import { v4 as uuidv4 } from 'uuid';

import { SearchContextModel } from './search-context.model';
import { KeywordGroupApiModel } from '../../../api/mentions/model/keyword-group-api.model';
import { SearchContextsEnum } from '../enums/search-contexts.enum';
import { BooleanSearchKeywordsModel } from './boolean-search-keywords.model';
import { ComparisonHelper } from '../helpers/comparison/comparison.helper';
import { searchContextToApiMap, searchContextFromApiMap } from './search-contexts.map';
import { operatorsEnum } from '../enums/operators.enum';

export class MonitoringKeywordGroupModel extends BooleanSearchKeywordsModel {
    collapsed?: boolean;

    constructor(apiModel?: KeywordGroupApiModel) {
        super();

        if (apiModel) {
            this.constructFromApi(apiModel);
        } else {
            this.constructDefault();
        }
    }

    constructFromApi(apiModel: KeywordGroupApiModel) {
        this.or = apiModel.anyTerms ?? [];
        this.and = apiModel.mustAllTerms ?? [];
        this.not = apiModel.mustNotTerms ?? [];
        this.searchContexts = apiModel.areas.map(
            area => new SearchContextModel(searchContextFromApiMap.get(area))
        );
    }

    constructDefault() {
        this.or = [];
        this.and = [];
        this.not = [];
        this.searchContexts = [
            new SearchContextModel(SearchContextsEnum.header),
            new SearchContextModel(SearchContextsEnum.lead),
            new SearchContextModel(SearchContextsEnum.body)
        ];
    }

    toApiEntity(): KeywordGroupApiModel {
        const model: KeywordGroupApiModel = {
            anyTerms: this.or.slice(),
            areas: this.searchContexts.map(
                searchContext => searchContextToApiMap.get(searchContext.value)
            ),
            mustAllTerms: this.and.slice(),
            mustNotTerms: this.not.slice()
        };

        return model;
    }

    /**
     * Determines whether or not the provided
     * search context is part of this keyword group
    */
    hasMatchingSearchContext(searchContext: SearchContextsEnum) {
        const hasSearchContexts: boolean = Array.isArray(this.searchContexts);
        let matchingSearchContexts: SearchContextModel[];
        let hasMatchingContext: boolean = false;

        if (hasSearchContexts) {
            matchingSearchContexts = where(this.searchContexts, { value: searchContext });
            hasMatchingContext = matchingSearchContexts.length > 0;
        }

        return hasMatchingContext;
    }

    /**
     * Checks that the keyword group's search context(s) is valid
     * @param group An active keyword group
     * @returns True if the group's search contexts will perform a valid search, false otherwise
     */
    keywordGroupHasValidSearchContext(): boolean {
        let imagesSelected = false;
        let textSelected = false;
        let valid = false;

        for (const context of this.searchContexts) {
            switch (context.value) {
                case SearchContextsEnum.images:
                    imagesSelected = true;
                    break;
                case SearchContextsEnum.header:
                case SearchContextsEnum.lead:
                case SearchContextsEnum.body:
                    textSelected = true;
                    break;
            }
        }
        if (imagesSelected && !textSelected) {
            // only images are selected, so it's valid
            valid = true;
        } else if (textSelected && !imagesSelected) {
            // text with a subcontext is selected, but images are not
            valid = true;
        }
        return valid;
    }

    /**
     * Determines if at least one keyword is set in a given keywordGroup
     *
     * @returns boolean
     */
    isAnyKeywordSet(): boolean {
        return this.and.length > 0 ||
            this.not.length > 0 ||
            this.or.length > 0;
    }

    /**
     * Gets a simpler representation of this object for use in comparison
     *
     * @returns Object - a simple object for use in comparison
     */
    getSimpleObject(): Object {
        return {
            or: this.or,
            and: this.and,
            not: this.not,
            searchContexts: this.searchContexts.map( searchContext => searchContext.name)
        };
    }

    /**
     * Returns a copy of this model
     *
     * @returns MonitoringKeywordGroupModel
     */
    clone(): MonitoringKeywordGroupModel {
        const clone = new MonitoringKeywordGroupModel(this.toApiEntity());

        return clone;
    }

    /**
     * Checks for equality between two keyword groups
     *
     * @param keywordGroup Object to compare to this
     *
     * @returns boolean
     */
    equals(keywordGroup: MonitoringKeywordGroupModel): boolean {
        if (!keywordGroup) {
            return false;
        }
        const comparisonHelper = new ComparisonHelper();

        if (!comparisonHelper.compareObjectsArraysForEquality(this.or, keywordGroup.or)) {
            return false;
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.and, keywordGroup.and)) {
            return false;
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.not, keywordGroup.not)) {
            return false;
        }

        // Easy check - if length different then we aren't the same
        if (this.searchContexts.length !== keywordGroup.searchContexts.length) {
            return false;
        }

        // Otherwise we do an in depth check, looking at which elements are enabled or not.
        let found: boolean;
        const haystackLength = this.searchContexts.length;
        const needleLength = keywordGroup.searchContexts.length;
        for (let i = 0; i < haystackLength; i++) {
            found = false;
            for (let j = 0; j < needleLength; j++) {
                if (this.searchContexts[i].value === keywordGroup.searchContexts[j].value) {
                    found = true;
                    break;
                }
            }
            if (!found) {
                return false;
            }
        }

        return true;
    }

    /**
     * Checks if an item can be added to the group
     * @param item An item to add to the value
     */
    canAddCurrentItem(item: string, operator: operatorsEnum): boolean {
        if (!item || item.trim().length === 0) {
            return false;
        }
        let exists: boolean;
        switch (operator) {
            case operatorsEnum.and:
                exists = this.and.includes(item);
                break;
            case operatorsEnum.not:
                exists = this.not.includes(item);
                break;
            case operatorsEnum.or:
                exists = this.or.includes(item);
                break;
        }
        return !exists;
    }
}
